import classNames from 'classnames';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import useDarkMode from '../../../hooks/useDarkMode';
import Header, { HeaderLeft, HeaderRight } from '../../../layout/Header/Header';
import { RootState } from '../../../store/store';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/icon/Icon';
import SettingsHeader from './SettingsHeader';
import Search from '../../../components/Search';
import ThemeContext from '../../../contexts/themeContext';
import DrawerNotifications from '../../notifications/components/DrawerNotifications';
import { useLocation } from 'react-router-dom';
import GoBack from '../../../components/GoBack';
import { SUPPORT_EMAIL, SUPPORT_MAILTO_LINK } from '../../../common/data/supportConfig';

const DefaultHeader = () => {
  const location = useLocation();
  const { t } = useTranslation(['menu', 'setup']);
  const { notificationsCount } = useSelector((state: RootState) => state.notifications);
  const { hideGlobalSearch } = useSelector((state: RootState) => state.appStore);
  const { darkModeStatus } = useDarkMode();
  const { mobileDesign } = useContext(ThemeContext);
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
  const [isSupportHovered, setIsSupportHovered] = useState(false);
  const styledBtn: IButtonProps = {
    color: darkModeStatus ? 'dark' : 'light',
    hoverShadow: 'default',
    isLight: !darkModeStatus,
    size: 'lg',
  };

  // Define the possible route names
  const tabNames: { [key: string]: string } = {
    '/Overview': 'Overview',
    '/setup/maintenance': 'Maintenance',
    '/reports': 'Reports',
    '/driverleadbord': 'Driver Leaderboard',
    '/Dispatch Management': 'Dispatch Management',
    '/add_task': 'Dispatch Management',
    '/setup/vehicles': 'Setup Admin',
    '/setup/alerts': 'Setup Admin',
    '/setup/users-list': 'Setup Admin',
    '/setup/roles': 'Setup Admin',
    '/setup/groups-list': 'Setup Admin',
    '/setup/geofences': 'Setup Admin',
    '/setup/drivers-list': 'Setup Admin',
    '/setup/alerts/edit-alert': 'Setup Admin',
    '/dispatchManagement/taskoverview': 'Dispatch Management',
    '/dispatchManagement/taskmonitoring': 'Dispatch Management',
    '/fleetSage': 'Fleet Sage',
    '/all-alerts': 'All Alerts',
  };

  // Determine the current tab name with AI Fleet Manager support
  const currentTab = location.pathname.startsWith('/ai-Fleet-manager')
    ? t('AI Fleet Manager')
    : t(tabNames[location.pathname] || 'Overview');

  // Conditionally render the header based on the current path
  if (location.pathname === '/my-fleet') {
    return null; // Don't render anything for '/my-fleet'
  }

  // Determine heading based on the current path
  const renderHeading = () => {
    if (location.pathname === '/settings/scheduled-reports') {
      return (
        <>
          <GoBack className="me-2" /> {t('Report')}
        </>
      );
    } else if (location.pathname.startsWith('/detaildriver/profil')) {
      return (
        <>
          <GoBack className="me-2" /> {t('Driver Leadership')}
        </>
      );
    } else if (location.pathname.startsWith('/detaildriver')) {
      return (
        <>
          <GoBack className="me-2" /> {t('Driver Leadership')}
        </>
      );
    } else if (location.pathname.startsWith('/add-vehicle')) {
      return (
        <>
          <GoBack className="me-2" /> {t('Setup Admin')}
        </>
      );
    } else if (location.pathname.startsWith('/vehicle-detail')) {
      return (
        <>
          <GoBack className="me-2" /> {t('Setup Admin')}
        </>
      );
    } else if (location.pathname.startsWith('/setup/alerts/create-alert')) {
      return (
        <>
          <GoBack className="me-2" /> {t('Setup Admin')}
        </>
      );
    } else if (location.pathname.startsWith('/setup/drivers/add-driver')) {
      return (
        <>
          <GoBack className="me-2" /> {t('Setup Admin')}
        </>
      );
    } else if (location.pathname.startsWith('/setup/alerts/edit-alert')) {
      return (
        <>
          <GoBack className="me-2" /> {t('Setup Admin')}
        </>
      );
    } else if (location.pathname.startsWith('/setup/drivers/edit-driver/')) {
      return (
        <>
          <GoBack className="me-2" /> {t('Setup Admin')}
        </>
      );
    }
    return currentTab;
  };

  return (
    <Header>
      {mobileDesign ? (
        <HeaderLeft>{!hideGlobalSearch && <Search />}</HeaderLeft>
      ) : (
        <>
          <HeaderLeft>
            <div className="d-flex align-items-center">
              <div
                className={classNames('fs-3', 'fw-bold', 'd-flex', 'align-items-center', {
                  'text-dark': !darkModeStatus,
                })}
              >
                {renderHeading()}
              </div>
            </div>
          </HeaderLeft>
          <HeaderRight>
            <div className="row g-3 custom-header-right">
              {!hideGlobalSearch && <Search />}

              {/* Notifications */}
              <div className="col-auto">
                <Button
                  {...styledBtn}
                  className="btn-only-icon"
                  data-tour="dark-mode"
                  onClick={() => {
                    setNotificationsModalOpen(!notificationsModalOpen);
                  }}
                >
                  <Icon icon="Notifications" className="btn-icon" id="notification-btn" />
                  {notificationsCount > 0 && (
                    <span className="position-absolute top-15 start-100 translate-middle badge rounded-pill bg-danger">
                      {notificationsCount === 99 ? '+' : notificationsCount}
                    </span>
                  )}
                </Button>
              </div>

              {/* Support */}
              <div className="col-auto">
                <div
                  style={{ position: 'relative', display: 'inline-block' }}
                  onMouseEnter={() => setIsSupportHovered(true)}
                  onMouseLeave={() => setIsSupportHovered(false)}
                >
                  <a
                    href={SUPPORT_MAILTO_LINK}
                    aria-label={`${t('Contact us')}: ${SUPPORT_EMAIL}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Button {...styledBtn} className="btn-only-icon">
                      <svg
                        id="support-email-btn"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
                        <rect x="2.5" y="13" width="4" height="6" rx="1.5" />
                        <rect x="17.5" y="13" width="4" height="6" rx="1.5" />
                        <path d="M19.5 19c0 1.7-1.8 3-4.5 3h-2" />
                      </svg>
                    </Button>
                  </a>

                  {isSupportHovered && (
                    <>
                      {/* hover bridge so the tooltip doesn't close when moving the mouse */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          width: 220,
                          height: 10,
                          pointerEvents: 'auto',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 10px)',
                          right: 0,
                          background: '#fff',
                          padding: '8px 14px',
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          border: '1px solid #f3c9d6',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                          pointerEvents: 'auto',
                        }}
                      >
                        <span style={{ color: '#111827' }}>{t('Contact us')}: </span>
                        <a
                          href={SUPPORT_MAILTO_LINK}
                          style={{
                            color: '#f00d69',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          {SUPPORT_EMAIL}
                        </a>
                        <div
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: 14,
                            width: 10,
                            height: 10,
                            background: '#fff',
                            borderLeft: '1px solid #f3c9d6',
                            borderTop: '1px solid #f3c9d6',
                            transform: 'rotate(45deg)',
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Settings */}
              <div className="col-auto">
                <SettingsHeader styledBtn={styledBtn} />
              </div>
            </div>
          </HeaderRight>
        </>
      )}

      {notificationsModalOpen && (
        <DrawerNotifications
          setIsModalOpen={setNotificationsModalOpen}
          isModalOpen={notificationsModalOpen}
        />
      )}
    </Header>
  );
};

export default DefaultHeader;